import { Injectable } from '@angular/core';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface OAuth2UserData {
  id: string;
  email: string;
  name: string;
  picture?: string;
  provider: 'google' | 'facebook' | 'apple';
}

@Injectable({ providedIn: 'root' })
export class OAuth2Service {
  private currentProvider = new BehaviorSubject<string | null>(null);
  private isRegisterMode = new BehaviorSubject<boolean>(false);

  constructor(private oauthService: OAuthService) {}

  initGoogleAuth(isRegister: boolean = false): Promise<void> {
    this.isRegisterMode.next(isRegister);
    this.currentProvider.next('google');
    
    const googleAuthConfig: AuthConfig = {
      issuer: environment.oauth2.google.issuer,
      strictDiscoveryDocumentValidation: false,
      clientId: environment.oauth2.google.clientId,
      redirectUri: `${window.location.origin}/auth/callback`,
      scope: 'openid profile email',
      responseType: 'code', // Authorization Code Flow
      showDebugInformation: environment.enableDebugInfo,
      timeoutFactor: 0.75,
      oidc: true,
      // PKCE desabilitado - backend não usa PKCE
      disablePKCE: true,
      // Não usar nonce para evitar problemas de validação
      skipIssuerCheck: false,
      requireHttps: false // Para desenvolvimento local
    };

    this.oauthService.configure(googleAuthConfig);
    return this.oauthService.loadDiscoveryDocumentAndTryLogin()
      .then(() => {})
      .catch(error => {
        throw error;
      });
  }

  initFacebookAuth(isRegister: boolean = false): Promise<void> {
    this.isRegisterMode.next(isRegister);
    this.currentProvider.next('facebook');
    
    const facebookAuthConfig: AuthConfig = {
      loginUrl: environment.oauth2.facebook.loginUrl,
      clientId: environment.oauth2.facebook.clientId,
      redirectUri: `${window.location.origin}/auth/callback`,
      scope: 'email public_profile',
      oidc: false // Facebook não é OIDC puro
    };

    this.oauthService.configure(facebookAuthConfig);
    return this.oauthService.tryLogin({})
      .then(() => {})
      .catch(error => {
        throw error;
      });
  }

  initAppleAuth(isRegister: boolean = false): Promise<void> {
    this.isRegisterMode.next(isRegister);
    this.currentProvider.next('apple');
    
    const appleAuthConfig: AuthConfig = {
      issuer: environment.oauth2.apple.issuer,
      clientId: environment.oauth2.apple.clientId,
      redirectUri: `${window.location.origin}/auth/callback`,
      scope: 'openid name email',
      strictDiscoveryDocumentValidation: false
    };

    this.oauthService.configure(appleAuthConfig);
    return this.oauthService.loadDiscoveryDocumentAndTryLogin()
      .then(() => {})
      .catch(error => {
        throw error;
      });
  }

  loginWithGoogle() {
    this.initGoogleAuth(false)
      .then(() => {
        // Salva o provider no sessionStorage E no localStorage como backup
        sessionStorage.setItem('oauth2_provider', 'google');
        sessionStorage.setItem('oauth2_register_mode', 'false');
        localStorage.setItem('oauth2_provider', 'google');
        localStorage.setItem('oauth2_register_mode', 'false');
        this.oauthService.initLoginFlow();
      })
      .catch(error => { throw error; });
  }

  loginWithFacebook() {
    this.initFacebookAuth(false)
      .then(() => {
        sessionStorage.setItem('oauth2_provider', 'facebook');
        sessionStorage.setItem('oauth2_register_mode', 'false');
        localStorage.setItem('oauth2_provider', 'facebook');
        localStorage.setItem('oauth2_register_mode', 'false');
        this.oauthService.initLoginFlow();
      })
      .catch(error => { throw error; });
  }

  loginWithApple() {
    this.initAppleAuth(false)
      .then(() => {
        sessionStorage.setItem('oauth2_provider', 'apple');
        sessionStorage.setItem('oauth2_register_mode', 'false');
        localStorage.setItem('oauth2_provider', 'apple');
        localStorage.setItem('oauth2_register_mode', 'false');
        this.oauthService.initLoginFlow();
      })
      .catch(error => { throw error; });
  }

  registerWithGoogle() {
    this.initGoogleAuth(true)
      .then(() => {
        sessionStorage.setItem('oauth2_provider', 'google');
        sessionStorage.setItem('oauth2_register_mode', 'true');
        localStorage.setItem('oauth2_provider', 'google');
        localStorage.setItem('oauth2_register_mode', 'true');
        this.oauthService.initLoginFlow();
      })
      .catch(error => { throw error; });
  }

  registerWithFacebook() {
    this.initFacebookAuth(true)
      .then(() => {
        sessionStorage.setItem('oauth2_provider', 'facebook');
        sessionStorage.setItem('oauth2_register_mode', 'true');
        localStorage.setItem('oauth2_provider', 'facebook');
        localStorage.setItem('oauth2_register_mode', 'true');
        this.oauthService.initLoginFlow();
      })
      .catch(error => { throw error; });
  }

  registerWithApple() {
    this.initAppleAuth(true)
      .then(() => {
        sessionStorage.setItem('oauth2_provider', 'apple');
        sessionStorage.setItem('oauth2_register_mode', 'true');
        localStorage.setItem('oauth2_provider', 'apple');
        localStorage.setItem('oauth2_register_mode', 'true');
        this.oauthService.initLoginFlow();
      })
      .catch(error => { throw error; });
  }

  logout() {
    this.oauthService.logOut();
    this.currentProvider.next(null);
    this.isRegisterMode.next(false);
    // Limpa também o localStorage
    localStorage.removeItem('oauth2_provider');
    localStorage.removeItem('oauth2_register_mode');
  }

  /**
   * Processa o callback OAuth2 após redirecionamento
   * Este método extrai o código de autorização da URL
   */
  async processCallback(): Promise<string | null> {
    // Extrai o código da URL (SEM decodificar - já vem decodificado pelo URLSearchParams)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    
    // Recupera o provider do sessionStorage ou localStorage como fallback
    let savedProvider = sessionStorage.getItem('oauth2_provider');
    let savedRegisterMode = sessionStorage.getItem('oauth2_register_mode') === 'true';
    
    // Se não encontrou no sessionStorage, tenta localStorage
    if (!savedProvider) {
      savedProvider = localStorage.getItem('oauth2_provider');
      savedRegisterMode = localStorage.getItem('oauth2_register_mode') === 'true';
    }
    
    if (error) {
      // Limpa storage em caso de erro
      sessionStorage.removeItem('oauth2_provider');
      sessionStorage.removeItem('oauth2_register_mode');
      localStorage.removeItem('oauth2_provider');
      localStorage.removeItem('oauth2_register_mode');
      throw new Error(`Erro na autenticação: ${error}`);
    }
    
    if (!code) {
      // Limpa storage em caso de erro
      sessionStorage.removeItem('oauth2_provider');
      sessionStorage.removeItem('oauth2_register_mode');
      localStorage.removeItem('oauth2_provider');
      localStorage.removeItem('oauth2_register_mode');
      throw new Error('Código de autorização não encontrado.');
    }
    
    if (!savedProvider) {
      throw new Error('Provider não encontrado. Por favor, inicie o login novamente.');
    }
    
    // Restaura o estado
    this.currentProvider.next(savedProvider);
    this.isRegisterMode.next(savedRegisterMode);
    
    // IMPORTANTE: Limpa ambos os storages SOMENTE DEPOIS de extrair o código
    sessionStorage.removeItem('oauth2_provider');
    sessionStorage.removeItem('oauth2_register_mode');
    localStorage.removeItem('oauth2_provider');
    localStorage.removeItem('oauth2_register_mode');
    
    return code;
  }

  get identityClaims() {
    return this.oauthService.getIdentityClaims();
  }

  get isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  get currentProvider$(): Observable<string | null> {
    return this.currentProvider.asObservable();
  }

  get isRegisterMode$(): Observable<boolean> {
    return this.isRegisterMode.asObservable();
  }

  /**
   * Extrai dados do usuário dos claims OAuth2
   */
  getUserData(): OAuth2UserData | null {
    const claims = this.identityClaims;
    
    if (!claims) {
      return null;
    }

    const provider = this.currentProvider.value || 'google';
    
    const userData = {
      id: claims['sub'] || claims['id'] || '',
      email: claims['email'] || '',
      name: claims['name'] || (claims['given_name'] ? `${claims['given_name']} ${claims['family_name'] || ''}`.trim() : '') || '',
      picture: claims['picture'] || claims['avatar_url'] || undefined,
      provider: provider as 'google' | 'facebook' | 'apple'
    };
    
    return userData;
  }

  /**
   * Verifica se o usuário está autenticado via OAuth2
   */
  isOAuth2Authenticated(): boolean {
    const isLoggedIn = this.isLoggedIn;
    const hasProvider = this.currentProvider.value !== null;
    const hasValidToken = this.oauthService.hasValidAccessToken();
    
    return isLoggedIn && hasProvider && hasValidToken;
  }

  /**
   * Limpa o estado OAuth2
   */
  clearState(): void {
    this.currentProvider.next(null);
    this.isRegisterMode.next(false);
  }
}
